function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

setTimeout(function () {
  $('.frcp-wishlist-nav-link__text').text('My wishlist');
}, 5000);


document.addEventListener("DOMContentLoaded", function () {

  setTimeout(() => {

    // Get all carousels
    const carousels = document.querySelectorAll('.shopify-section.section .jdgm-cards-carousel');

    // Select the second one
    const carousel = carousels[1];

    if (carousel) {
      carousel.setAttribute("id", "carousal");
    }

    // Convert header div into anchor
    const header = document.querySelector('.product .jdgm-cards-carousel .jdgm-header');

    if (header) {
      const link = document.createElement("a");

      link.innerHTML = header.innerHTML;
      link.href = "#carousal";
      link.className = header.className;

      header.replaceWith(link);
    }

  }, 3000);

});

// ============================================================
// ✅ FREE GIFT ITEM — DISPLAY ORDER FIX
// ------------------------------------------------------------
// Shopify apna cart data (cart.js / cart.items) hamesha ADD-ORDER me
// serve nahi karta — line items apne internal "key" (jo variant id se
// shuru hota hai) ke hisaab se store/serve hote dikhte hain. Free-gift
// variant ka id numerically chhota hai, isliye Shopify ka apna cart
// data usko hamesha upar rakh deta hai — chahe use main product ke
// BAAD add kiya gaya ho, aur chahe hum add-to-cart calls kitni bhi
// carefully sequential/queued kyun na bhejein. Ye cheez client-side se
// control nahi ho sakti kyunki asli data hi Shopify is order me deta
// hai.
//
// Fix: raw data order ko badalne ki koshish karne ke bajaye, hum sirf
// DISPLAY (DOM) me har free-gift row ko uske parent product row ke
// TURANT BAAD move kar dete hain. Cart templates (main-cart-items.liquid,
// cart-drawer.liquid) har row ko `data-variant-id` dete hain, aur agar
// wo ek free-gift item hai to ek extra `data-gift-for="<parent-variant-
// id>"` attribute bhi dete hain. Yahan hum:
//   1) Page load par ek baar reorder karte hain.
//   2) Cart ke kisi bhi AJAX re-render (quantity change, naya item add,
//      remove, etc.) ke baad bhi dobara reorder karte hain — iske liye
//      un stable containers (#main-cart-items, <cart-drawer>) par
//      MutationObserver laga diya hai jinke ANDAR ka content Shopify
//      ki Section Rendering API baar-baar replace karti hai, lekin
//      khud wo container element kabhi destroy nahi hota.
// ============================================================
window.reorderGiftItemsInContainer = function (container) {
  if (!container) return;

  function getRowWithCompanion(row) {
    var group = [row];
    var next = row.nextElementSibling;
    if (next && next.classList && next.classList.contains('divider-tr')) {
      group.push(next);
    }
    return group;
  }

  // Group all gift rows by their parent key first
  var giftRows = Array.prototype.slice.call(container.querySelectorAll('[data-gift-for]'));
  var byParent = {};
  giftRows.forEach(function (row) {
    var parentId = row.getAttribute('data-gift-for');
    if (!parentId) return;
    (byParent[parentId] = byParent[parentId] || []).push(row);
  });

  var mutated = false;

  Object.keys(byParent).forEach(function (parentId) {
    var parentRow = container.querySelector(
      '[data-variant-id="' + parentId + '"]:not([data-gift-for])'
    );
    if (!parentRow) return;

    var parentGroup = getRowWithCompanion(parentRow);
    var insertionPoint = parentGroup[parentGroup.length - 1];

    // Insert ALL of this parent's gifts, in one pass, in stable DOM order
    byParent[parentId].forEach(function (giftRow) {
      var giftGroup = getRowWithCompanion(giftRow);

      // Already right after insertionPoint? skip (no mutation)
      if (insertionPoint.nextElementSibling === giftGroup[0]) {
        insertionPoint = giftGroup[giftGroup.length - 1];
        return;
      }

      var parent = insertionPoint.parentNode;
      var referenceNode = insertionPoint.nextSibling;
      giftGroup.forEach(function (el) {
        parent.insertBefore(el, referenceNode);
      });
      mutated = true;
      insertionPoint = giftGroup[giftGroup.length - 1];
    });
  });

  return mutated;
};

(function () {
  var GIFT_REORDER_SELECTORS = ['#main-cart-items', 'cart-drawer'];

  function attachGiftReorderObserver(el) {
    if (!el || el.__mappleGiftObserverAttached) return;
    el.__mappleGiftObserverAttached = true;

    window.reorderGiftItemsInContainer(el);

    var observer = new MutationObserver(function () {
      window.reorderGiftItemsInContainer(el);
    });

    observer.observe(el, { childList: true, subtree: true });
  }

  function initGiftReorderObservers() {
    GIFT_REORDER_SELECTORS.forEach(function (selector) {
      attachGiftReorderObserver(document.querySelector(selector));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGiftReorderObservers);
  } else {
    initGiftReorderObservers();
  }
})();

// (function () {
//   var GIFT_REORDER_SELECTORS = ['#main-cart-items', 'cart-drawer'];

//   function attachGiftReorderObserver(el) {
//     if (!el || el.__mappleGiftObserverAttached) return;
//     el.__mappleGiftObserverAttached = true;

//     window.reorderGiftItemsInContainer(el);

//     var observer = new MutationObserver(function () {
//       window.reorderGiftItemsInContainer(el);
//     });

//     observer.observe(el, { childList: true, subtree: true });
//   }

//   function initGiftReorderObservers() {
//     GIFT_REORDER_SELECTORS.forEach(function (selector) {
//       attachGiftReorderObserver(document.querySelector(selector));
//     });
//   }

//   if (document.readyState === 'loading') {
//     document.addEventListener('DOMContentLoaded', initGiftReorderObservers);
//   } else {
//     initGiftReorderObservers();
//   }
// })();




// ============================================================
// ANNIVERSARY OFFER MODAL — single source of truth
// Ye function pehle 4 files me duplicate tha. Ab sirf yahan hai.
// ============================================================
window.toggleAnnivModal = function (open) {
  var isMobile = window.innerWidth < 768;
  var $modal   = $('#anniv-modal');
  var $overlay = $('#anniv-overlay');

  if (!$modal.length || !$overlay.length) return;

  if (open) {
    var mainProduct = ($modal.attr('selected-model') || '')
      .replace(/\s+/g, '').toLowerCase();

    var found = false;

    $('.case .anniv-card').each(function () {
      var cardModel = ($(this).attr('product-model') || '')
        .replace(/\s+/g, '').toLowerCase();

      if (mainProduct && cardModel && cardModel === mainProduct) {
        $(this).css('display', 'block');
        found = true;
      } else {
        $(this).css('display', 'none');
      }
    });

    $('.case').css('display', found ? '' : 'none');

    $overlay.css('display', 'flex');
    $modal.css('transform', isMobile ? 'translateY(0)' : 'translateX(0)');
    document.body.style.overflow = 'hidden';
  } else {
    $overlay.css('display', 'none');
    $modal.css('transform', isMobile ? 'translateY(100%)' : 'translateX(100%)');
    document.body.style.overflow = '';
  }
};

// Ek hi delegated handler — har section me dobara bind karne ki zarurat nahi
$(document).on('click', '.anniv-btn, #anniv-btn, #anniv-btn-buy', function () {
  var $btn = $(this);

  window.alreadyAdded      = $btn.data('added');
  window.selectedVariantId = $btn.data('variant-id');
  window.selectedComboId   = $btn.data('combo-id');
  window.selectedModel     = $btn.data('product-model') || '';
 console.log('window.selectedVariantId', window.selectedVariantId);
  $('#anniv-modal')
    .attr('selected-model', window.selectedModel)
    .attr('data-intent', $btn.is('#anniv-btn-buy') ? 'buy' : 'cart');

  window.toggleAnnivModal(true);
});

$(document).on('click', '#anniv-overlay', function (e) {
  if (e.target === this) window.toggleAnnivModal(false);
});



// ============================================================
// ANNIV-CART-GLOBAL.JS
// ------------------------------------------------------------
// Ek hi jagah par:
//   1. Shared cart helpers (queue, cart state, free-gift logic)
//   2. Anniversary accessory modal ka POORA behaviour
//   3. Maple OTP login popup ke saath handoff (guest user)
//
// Ab Handpick-collection / main-collection-product-grid /
// main-search / kisi bhi naye section me ye logic dobara likhne
// ki zaroorat nahi. Sirf ye file load karo:
//
//   <script src="{{ 'anniv-cart-global.js' | asset_url }}" defer="defer"></script>
//
// ...aur us section ka purana inline cart/anniv script block
// hata do. Modal ka markup ({% render 'anniversary-offer-popup-homepage' %})
// page par EK BAAR render hona chahiye — IDs use hoti hain.
// ============================================================

(function () {
  'use strict';

  if (window.__annivCartGlobalReady) return;
  window.__annivCartGlobalReady = true;

  // ============================================================
  // 1. CONFIG
  // ============================================================

//  window.AUTO_ADD_VARIANT_ID = window.AUTO_ADD_VARIANT_ID || 51476743520554;

// window.FREE_GIFT_ITEMS = [
//   { id: window.AUTO_ADD_VARIANT_ID, excludedTypes: ['accessories', 'accessory'] }
// ];

  // Free Travel Pass in product types par NAHI jaata
  // var EXCLUDED_TYPES = ['accessories', 'accessory'];

  // Anniversary modal se add hone wale main product ka type.
  // (Wahan hamesha iPhone/pre-owned device hi hota hai.)
  var ANNIV_PRODUCT_TYPE = 'iPhone';

  // ============================================================
  // 2. SHARED CART HELPERS
  // ------------------------------------------------------------
  // Guarded (`if (!window.x)`) hain taaki product-form.js pehle load
  // ho to uski definitions chalti rahein — dono identical hain.
  // ============================================================

  // ✅ Free item ko uske PARENT CART LINE se attach karte hain.
  // parent_id sirf tab valid hai jab parent USI request me add ho raha ho.
  // Yahan parent pehle se cart me hota hai, isliye parent_line_key chahiye —
  // warna Shopify 422 deta hai: "Cannot reference existing parent lines by
  // variant_id."
 // ✅ Free items ko unke PARENT CART LINE se attach karte hain.
// Ek parent ke multiple children ho sakte hain — sab ek hi
// parent_line_key point karte hain.
if (!window.makeFreeItems) {
  window.makeFreeItems = function (parentLineKey, productType, extraVariantIds) {
  var type = String(productType || '').trim().toLowerCase();

  function build(id, qty) {
    return {
      id: Number(id),
      quantity: qty || 1,
      parent_line_key: parentLineKey,
      properties: {
        _free_gift: 'true',
        _gift_for: String(parentLineKey)
      }
    };
  }

  var items = (window.FREE_GIFT_ITEMS || [])
    .filter(function (gift) {
      return (gift.excludedTypes || []).indexOf(type) === -1;
    })
    .map(function (gift) { return build(gift.id, gift.quantity); });

  // Modal se chuna hua accessory — type filter isspar lagu nahi hota
  (extraVariantIds || []).forEach(function (id) {
    if (id) items.push(build(id));
  });

  return items;
};
}

// Purana API surface — pehla gift return karta hai, null agar koi nahi.
if (!window.makeFreeItem) {
  window.makeFreeItem = function (parentLineKey, productType) {
    return window.makeFreeItems(parentLineKey, productType)[0] || null;
  };
}

    // Koi bhi line jo free gift hai — fixed gift ya modal se chuna accessory.
  // Variant ID par match nahi kar sakte kyunki accessory har baar badalta hai,
  // isliye _free_gift property dekhte hain.
  window.isFreeGiftLine = function (item) {
    var props = item.properties || {};
    if (props._free_gift === 'true') return true;
    return (window.FREE_GIFT_ITEMS || []).some(function (g) {
      return Number(g.id) === Number(item.variant_id);
    });
  };

  if (!window.buildCartState) {
    window.buildCartState = function (cart) {
      var count = 0;
      var hasAutoItem = false;

      (cart.items || []).forEach(function (item) {
        if (window.isFreeGiftLine(item)) {
          hasAutoItem = true;
        } else {
          count += item.quantity;
        }
      });

      window.__cachedCartCount = count;
      return { count: count, hasAutoItem: hasAutoItem };
    };
  }

  if (!window.getCartState) {
    window.getCartState = function (force) {
      if (!force && window.__cachedCartState) {
        return Promise.resolve(window.__cachedCartState);
      }

      return fetch('/cart.js')
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          window.__cachedCartState = window.buildCartState(cart);
          return window.__cachedCartState;
        })
        .catch(function (e) {
          console.error('Cart fetch error:', e);
          return { count: 0, hasAutoItem: false, error: true };
        });
    };
  }

  // ------------------------------------------------------------
  // GLOBAL SERIAL QUEUE for /cart/add.js
  // Ek waqt me sirf EK request Shopify ko jaati hai, taaki cart me
  // line-item order hamesha wahi mile jis order me add kiya gaya:
  // main1, free1, main2, free2 ...
  // ------------------------------------------------------------
  window.__mappleCartAddQueue = window.__mappleCartAddQueue || Promise.resolve();

  if (!window.queueCartAdd) {
    window.queueCartAdd = function (items) {
      var run = function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        });
      };

      // Pichli call fail ho ya pass, uske BAAD hi agli chalegi.
      var result = window.__mappleCartAddQueue.then(run, run);
      window.__mappleCartAddQueue = result.catch(function () {});
      return result;
    };
  }

  if (!window.addItemsToCartSafe) {
    window.addItemsToCartSafe = function (items) {
      return window.queueCartAdd(items);
    };
  }

  // ✅ FREE GIFT — main items ke SUCCESS ke BAAD, ALAG call me.
  // Pehla argument /cart/add.js ka RESPONSE hai; usme se parent line
  // ka `key` nikaal kar gift usse attach karte hain (extra /cart.js
  // fetch ki zaroorat nahi).
  //
  // Free variant sold out / unpublished ho to error yahin swallow ho
  // jaata hai taaki main add-to-cart flow break na ho.
   // ✅ FREE GIFTS — main items ke SUCCESS ke BAAD, alag call me.
  // Har gift apni call me jaata hai: /cart/add.js atomic hai, to ek array
  // me dono bhejne par ek fail hone se doosra bhi chala jaata tha.
  window.addFreeGiftItemSafe = function (addResponse, mainVariantId, productType, extraVariantIds) {
  var added = (addResponse && addResponse.items) || [];
  var parentLine = added.filter(function (i) {
    return String(i.variant_id) === String(mainVariantId);
  })[0];

  if (!parentLine || !parentLine.key) return Promise.resolve(null);

  var freeItems = window.makeFreeItems(parentLine.key, productType, extraVariantIds);
  if (!freeItems.length) return Promise.resolve(null);

  return freeItems.reduce(function (chain, item) {
    return chain.then(function () {
      return window.queueCartAdd([item]).catch(function (err) {
        console.warn('Free gift failed:', item.id, err);
        return null;
      });
    });
  }, Promise.resolve());
};

  // Purana API surface (dusre sections isko call karte hain)
  if (!window.checkCartLimit) {
    window.checkCartLimit = function (limit) {
      limit = limit || 4;
      return window.getCartState().then(function (state) {
        return { allowed: state.count < limit, count: state.count };
      });
    };
  }

  // ============================================================
  // 3. MAPLE OTP LOGIN — GUEST HANDOFF
  // ------------------------------------------------------------
  // Guest jab modal se "Proceed"/"Skip" dabata hai, add-to-cart guard
  // uski /cart/add.js call pakad kar login popup khol deta hai. Lekin
  // us waqt modal khula hota hai aur popup uske PEECHE chhup jaata hai
  // (popup z-index 998/999). Isliye:
  //   a) popup ko sabse upar utha dete hain (CSS neeche inject hoti hai)
  //   b) guest ka pata chalte hi modal band kar dete hain
  // ============================================================

  // Guard sirf logged-out par khud ko ready mark karta hai — yahi guest
  // hone ka sabse bharosemand signal hai.
  window.mappleIsGuest = function () {
    return window.__mappleCartGuardReady === true;
  };

  window.__closeAnnivForLogin = function () {
    try {
      if (typeof window.toggleAnnivModal === 'function') window.toggleAnnivModal(false);
    } catch (e) {}

    var overlay = document.getElementById('anniv-overlay');
    if (overlay) overlay.style.display = 'none';

    var modal = document.getElementById('anniv-modal');
    if (modal) modal.classList.remove('active', 'open', 'show');

    document.body.style.overflow = '';
  };

  function injectLoginPopupCss() {
    if (document.getElementById('anniv-mapple-zfix')) return;
    var style = document.createElement('style');
    style.id = 'anniv-mapple-zfix';
    style.textContent =
      '.mapple-overlay{z-index:2147483000!important}' +
      '.mapple-tag-wrap{z-index:2147483001!important}';
    (document.head || document.documentElement).appendChild(style);
  }

  // ============================================================
  // 4. ANNIVERSARY MODAL — SHARED CONTROLLER
  // ------------------------------------------------------------
  // Saare listeners DOCUMENT par delegated hain, isliye modal markup
  // kab render hua (page load, section reload, quick-add) — is se koi
  // farak nahi padta.
  // ============================================================

  window.selectedVariantId = window.selectedVariantId || null;
  window.selectedModel = window.selectedModel || null;
  window.selectedComboId = window.selectedComboId || null;
  window.alreadyAdded = window.alreadyAdded || 'false';

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function $id(id) { return document.getElementById(id); }

  // ---- "already in cart" popup (drawer ke center me) ----
  function showAnnivExistsPopup() {
    var el = $id('anniv-exists-popup');
    if (!el) { alert(ALREADY_IN_CART_MSG); return; }

    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');

    var pageOverlay = $id('anniv-exists-page-overlay');
    if (pageOverlay) pageOverlay.classList.add('is-open');
  }

  function hideAnnivExistsPopup() {
    var el = $id('anniv-exists-popup');
    if (el) {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
    }

    var pageOverlay = $id('anniv-exists-page-overlay');
    if (pageOverlay) pageOverlay.classList.remove('is-open');
  }

    // ---- "already in cart" check + inline error ----
  function annivVariantInCart(variantId) {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        return (cart.items || []).some(function (item) {
          // free-gift lines don't count — wahi accessory gift ke roop me
          // pehle se ho sakta hai, usse main add block nahi hona chahiye
          if (window.isFreeGiftLine(item)) return false;
          return String(item.variant_id) === String(variantId);
        });
      })
      .catch(function () { return false; }); // cart fetch fail = flow block mat karo
  }

  function annivErrorEl() {
    var el = $id('anniv-error');
    if (el) return el;

    var anchor = $id('anniv-final') || document.querySelector('.button-skip-atc');
    if (!anchor || !anchor.parentNode) return null;

    el = document.createElement('p');
    el.id = 'anniv-error';
    el.className = 'anniv-inline-error';
    el.setAttribute('role', 'alert');
    anchor.parentNode.insertBefore(el, anchor);
    return el;
  }

  function showAnnivError(msg) {
    var el = annivErrorEl();
    if (!el) { alert(msg); return; }
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._hideTimer);
    el._hideTimer = setTimeout(function () {
      el.classList.remove('is-visible');
    }, 4000);
  }

  function hideAnnivError() {
    var el = $id('anniv-error');
    if (el) el.classList.remove('is-visible');
  }

  // ---- add-to-cart chalane ka ek hi rasta ----
  // ---- add-to-cart chalane ka ek hi rasta ----
  function runAnnivAdd(mainItem, accessoryVariantId, onFailure) {
    // Duplicate guard — Proceed aur Skip dono yahin se guzarte hain
    return annivVariantInCart(mainItem.id).then(function (exists) {
      if (exists) {
        showAnnivExistsPopup();
        if (typeof onFailure === 'function') onFailure();
        return null;
      }

      // Guest ka add-to-cart guard is call ko reject kar dega, us waqt tak
      // accessory wali call bheji hi nahi jaati. Isliye chuna hua accessory
      // yahan rakh dete hain — guard ise pending list me khud jod lega.
      window.__mappleExtraGiftVariantIds = accessoryVariantId ? [Number(accessoryVariantId)] : [];

      if (window.mappleIsGuest()) window.__closeAnnivForLogin();

      return window
        .addItemsToCartSafe([mainItem])
        .then(function (res) {
          return window.addFreeGiftItemSafe(
            res,
            mainItem.id,
            ANNIV_PRODUCT_TYPE,
            accessoryVariantId ? [accessoryVariantId] : []
          );
        })
        .then(function () {
          window.__mappleExtraGiftVariantIds = [];
          window.__cachedCartState = null;
          window.location = '/cart';
        })
        .catch(function (err) {
          console.error(err);
          if (typeof onFailure === 'function') onFailure();
          if (window.mappleIsGuest()) window.__closeAnnivForLogin();
        });
    });
  }

function onProceed() {
  var btn = $id('anniv-final');
  if (!btn || btn.disabled) return;

  var acc = document.querySelector('input[name="anniv_accessory"]:checked');
  if (!acc) { alert('Select an accessory'); return; }

  btn.disabled = true;

  runAnnivAdd(
    {
      id: Number(window.selectedVariantId),
      quantity: 1,
      properties: { _anniversary_offer: 'true' }
    },
    Number(acc.value),
    function () { btn.disabled = false; }
  );
}

function onSkip(btn) {
  if (!btn || btn.disabled) return;
  btn.disabled = true;

  runAnnivAdd(
    {
      id: Number(window.selectedVariantId),
      quantity: 1,
      properties: { _anniversary_offer: 'true' }
    },
    null,                                  // Skip = sirf fixed gift
    function () { btn.disabled = false; }
  );
}

  // ---- card selection ----
  function selectCard(card) {
    if (card.classList.contains('disabled')) return;

    var cards = document.querySelectorAll('.anniv-card');
    var proceedBtn = $id('anniv-final');
    var selectedBox = $id('selected-product-name');
    var selectedName = document.querySelector('.selected-name');

    Array.prototype.forEach.call(cards, function (c) {
      c.classList.remove('active');
      var input = c.querySelector('input');
      if (input) input.checked = false;
      if (c !== card) c.classList.add('disabled');
    });

    card.classList.add('active');
    var ownInput = card.querySelector('input');
    if (ownInput) ownInput.checked = true;

    var titleEl = card.querySelector('.anniv-product-title');
    var imageEl = card.querySelector('.anniv-product-image');
    var urlEl = card.querySelector('.anniv-product-url');

    if (selectedName && titleEl) selectedName.textContent = titleEl.textContent.trim();

    var selectedImage = $id('selected-product-image');
    if (selectedImage && imageEl) {
      selectedImage.src = imageEl.getAttribute('src');
      selectedImage.alt = titleEl ? titleEl.textContent.trim() : '';
    }

    var selectedLink = $id('selected-product-link');
    if (selectedLink && urlEl) selectedLink.href = urlEl.value;

    if (selectedBox) selectedBox.style.display = 'flex';
    if (proceedBtn) proceedBtn.disabled = false;
  }

  function clearSelection() {
    var cards = document.querySelectorAll('.anniv-card');
    var proceedBtn = $id('anniv-final');
    var selectedBox = $id('selected-product-name');
    var selectedName = document.querySelector('.selected-name');

    Array.prototype.forEach.call(cards, function (c) {
      c.classList.remove('active', 'disabled');
      var input = c.querySelector('input');
      if (input) input.checked = false;
    });

    if (selectedName) selectedName.textContent = '';
    if (selectedBox) selectedBox.style.display = 'none';
    if (proceedBtn) proceedBtn.disabled = true;
  }

  // ---- modal kholne wala button ----
  function onAnnivBtn(btn) {
    window.selectedVariantId = btn.getAttribute('data-variant-id');
    window.selectedModel = btn.getAttribute('data-product-model');
    window.selectedComboId = btn.getAttribute('data-combo-id');
    window.alreadyAdded = btn.getAttribute('data-added');

    var modal = $id('anniv-modal');
    if (modal && window.selectedModel != null) {
      modal.setAttribute('selected-model', window.selectedModel);
    }

    clearSelection();

    try {
      if (typeof window.toggleAnnivModal === 'function') window.toggleAnnivModal(true);
    } catch (e) {
      console.error('toggleAnnivModal error:', e);
    }

    updateAnnivCounters();
  }

  // ---- card counter (x / y) ----
  function updateAnnivCounters() {
    document.querySelectorAll('.anniv-section').forEach(function (section) {
      var grid = section.querySelector('.anniv-grid');
      var cards = section.querySelectorAll('.anniv-card');
      var currentDesktop = section.querySelector('.current-card.desktop');
      var currentMobile = section.querySelector('.current-card.mobile');
      var total = section.querySelector('.total-cards');

      if (!grid || (!currentDesktop && !currentMobile) || !total) return;

      total.textContent = cards.length;

      var lastVisible = 0;
      var gridRect = grid.getBoundingClientRect();

      Array.prototype.forEach.call(cards, function (card, index) {
        var r = card.getBoundingClientRect();
        if (r.left < gridRect.right && r.right > gridRect.left) lastVisible = index + 1;
      });

      if (currentDesktop) currentDesktop.textContent = lastVisible;
      if (currentMobile) currentMobile.textContent = Math.max(1, lastVisible);

      if (!grid.dataset.annivCounterBound) {
        grid.dataset.annivCounterBound = '1';
        grid.addEventListener('scroll', updateAnnivCounters);
      }
    });
  }

  // ---- accessory search ----
  function filterAnnivCards(keyword) {
    var q = String(keyword || '').trim().toLowerCase();
    document.querySelectorAll('.anniv-card').forEach(function (card) {
      var title = (card.dataset.title || '').toLowerCase();
      var sku = (card.dataset.sku || '').toLowerCase();
      card.style.display =
        q === '' || title.indexOf(q) !== -1 || sku.indexOf(q) !== -1 ? '' : 'none';
    });
  }

  // ============================================================
  // 5. DELEGATED LISTENERS (ek hi baar bindte hain)
  // ============================================================

  document.addEventListener('click', function (e) {
    var t = e.target;

    var annivBtn = closest(t, '.anniv-btn');
    if (annivBtn) { onAnnivBtn(annivBtn); return; }

    if (closest(t, '#remove-selected-product')) { clearSelection(); return; }

    if (closest(t, '#anniv-final')) { onProceed(); return; }

    var skip = closest(t, '.button-skip-atc');
    if (skip) { onSkip(skip); return; }

    var card = closest(t, '.anniv-card');
    if (card) { selectCard(card); return; }

    // overlay par click = modal band
    if (t && t.id === 'anniv-overlay') {
      try {
        if (typeof window.toggleAnnivModal === 'function') window.toggleAnnivModal(false);
      } catch (err) {}
      t.style.display = 'none';
    }
  });

  document.addEventListener('input', function (e) {
    if (e.target && e.target.classList.contains('anniv-search')) {
      filterAnnivCards(e.target.value);
    }
  });

  // ============================================================
  // 6. BOOT
  // ============================================================

  function boot() {
    injectLoginPopupCss();

    var proceedBtn = $id('anniv-final');
    if (proceedBtn) proceedBtn.disabled = true;

    var selectedBox = $id('selected-product-name');
    if (selectedBox) selectedBox.style.display = 'none';

    updateAnnivCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify theme editor / dynamic section load
  document.addEventListener('shopify:section:load', function () {
    setTimeout(boot, 100);
  });
})();


// ============================================================
// GATED VARIANT — 52558810644778 akela cart me nahi reh sakta
// ------------------------------------------------------------
// Rule: agar cart me sirf EK real product hai aur wo ye variant hai,
// to use automatically remove kar do. Free-gift lines ko count nahi
// karte (wo apne parent ke saath auto-add hoti hain), aur gift ko
// parent ke saath hi remove kar dete hain.
// ============================================================
(function () {
  'use strict';

  if (window.__gatedVariantGuardReady) return;
  window.__gatedVariantGuardReady = true;

  var GATED_VARIANT_ID = 52558810644778;
  var busy = false;

  function isFreeGift(item) {
    var props = item.properties || {};
    return (
      Number(item.variant_id) === Number(window.AUTO_ADD_VARIANT_ID) ||
      props._free_gift === 'true'
    );
  }

  // Gift lines jo is parent se judi hain (properties._gift_for = parent key)
  function giftKeysFor(cart, parentKey) {
    return (cart.items || [])
      .filter(function (item) {
        var props = item.properties || {};
        return isFreeGift(item) && String(props._gift_for) === String(parentKey);
      })
      .map(function (item) { return item.key; });
  }

  function refreshCartUI() {
    var path = window.location.pathname;

    // Cart page par sabse safe reload hai — totals, discounts sab sync ho jaate hain
    if (path === '/cart' || path.indexOf('/cart') === 0) {
      window.location.reload();
      return;
    }

    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (res) { return res.json(); })
      .then(function (sections) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
          var fresh = parsed.querySelector('cart-drawer');
          if (fresh) drawer.innerHTML = fresh.innerHTML;
        }

        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          var bubbleDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
          var freshBubble = bubbleDoc.querySelector('#cart-icon-bubble');
          if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
        }
      })
      .catch(function (e) { console.warn('Cart UI refresh failed:', e); });
  }

  window.enforceGatedVariant = function () {
    if (busy) return Promise.resolve(false);
    busy = true;

    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var realItems = (cart.items || []).filter(function (item) {
          return !isFreeGift(item);
        });

        // Sirf tab hatao jab EK hi real product ho aur wahi gated variant ho
        if (realItems.length !== 1) return false;
        if (Number(realItems[0].variant_id) !== GATED_VARIANT_ID) return false;

        var updates = {};
        updates[realItems[0].key] = 0;
        giftKeysFor(cart, realItems[0].key).forEach(function (key) {
          updates[key] = 0;
        });

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (res) { return res.json(); })
          .then(function () {
            window.__cachedCartState = null;
            window.__cachedCartCount = null;
            refreshCartUI();
            return true;
          });
      })
      .catch(function (e) {
        console.warn('Gated variant check failed:', e);
        return false;
      })
      .then(function (removed) {
        busy = false;
        return removed;
      });
  };

  // ---- Kab chalega ----

  // 1) Page load par
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.enforceGatedVariant);
  } else {
    window.enforceGatedVariant();
  }

  // 2) Theme ke cart update events par
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      setTimeout(window.enforceGatedVariant, 150);
    });
  }
  document.addEventListener('cart:refresh', function () {
    setTimeout(window.enforceGatedVariant, 150);
  });

  // 3) Checkout par last guard — akela gated item hua to checkout block
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest
        ? e.target.closest('[name="checkout"], .cart__checkout-button, #checkout')
        : null;
      if (!btn) return;

      if (btn.dataset.gatedChecked === '1') {
        delete btn.dataset.gatedChecked;
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      window.enforceGatedVariant().then(function (removed) {
        if (removed) return; // item hata diya, checkout nahi hoga
        btn.dataset.gatedChecked = '1';
        btn.click();
      });
    },
    true
  );
})();



